import { apiRequest } from './request'
import type { ApiResponse } from './types'

// ============================================================================
// 类型定义
// ============================================================================

/**
 * 基础菜单数据接口
 */
export interface BaseMenuData {
  name: string
  path: string
  icon?: string
  iconColor?: string
  visible: boolean
  parentId: number
  permissionCode?: string
  sort?: number
}

/**
 * 保存菜单请求接口（新增或修改）
 */
export interface SaveMenuRequest extends BaseMenuData {
  id?: number // 新增时为undefined或0，修改时传入具体ID
}

/**
 * 更新菜单排序请求
 */
export interface UpdateMenuOrderRequest {
  menuId: number
  sort: number
}

/**
 * 批量更新菜单排序请求（用于拖拽排序）
 */
export interface BatchUpdateMenuOrderRequest {
  menuOrders: UpdateMenuOrderRequest[]
}



/**
 * 菜单项接口（包含ID和时间戳）
 */
export interface MenuItem extends BaseMenuData {
  id: number
  createdAt: string
  updatedAt?: string
  sort?: number
  children?: MenuItem[]
  updating?: boolean // 用于跟踪更新状态
  level?: number // 层级深度
  hasChildren?: boolean // 是否有子菜单
  expanded?: boolean // 是否展开
}

/**
 * 菜单树项接口（用于树形结构）
 */
export interface MenuTreeItem {
  id: number
  parentId: number
  name: string
  path: string
  icon?: string
  iconColor?: string
  visible: boolean
  permissionCode?: string
  sort?: number
  hasPermission?: boolean
  children?: MenuTreeItem[]
}

/**
 * 权限树节点类型
 */
export interface PermissionTreeNode {
  name: string
  code: string
  type: string
  permissionCode?: string
  permissionName?: string
  permissionType?: string
  apiPath?: string
  httpMethod?: string
  children?: PermissionTreeNode[]
}

// ============================================================================
// 菜单管理API
// ============================================================================

export const menuApi = {
  // 获取菜单树
  getMenuTree: (): Promise<ApiResponse<MenuTreeItem[]>> => {
    return apiRequest.get('/api/PlatformMenu/tree')
  },

  // 获取用户可访问的菜单树
  getMyMenus: (): Promise<ApiResponse<MenuTreeItem[]>> => {
    return apiRequest.get('/api/PlatformMenu/my-menus')
  },

  // 获取权限树
  getPermissionTree: (): Promise<ApiResponse<PermissionTreeNode[]>> => {
    return apiRequest.get('/api/Permission/tree')
  },

  // 获取菜单详情
  getMenuDetail: (id: number): Promise<ApiResponse<MenuItem>> => {
    return apiRequest.get(`/api/PlatformMenu/${id}`)
  },

  // 保存菜单（新增或修改）
  saveMenu: (data: SaveMenuRequest): Promise<ApiResponse<MenuItem>> => {
    return apiRequest.post('/api/PlatformMenu', data)
  },

  // 删除菜单
  deleteMenu: (id: number): Promise<ApiResponse<boolean>> => {
    return apiRequest.delete(`/api/PlatformMenu/${id}`)
  },


  // 更新菜单显示状态
  updateMenuVisibility: (id: number, visible: boolean): Promise<ApiResponse<boolean>> => {
    return apiRequest.put(`/api/PlatformMenu/${id}/visibility`, { visible })
  },

  // 批量更新菜单排序（用于拖拽排序）
  batchUpdateMenuSort: (request: BatchUpdateMenuOrderRequest): Promise<ApiResponse<boolean>> => {
    return apiRequest.put('/api/PlatformMenu/batch-sort', request)
  },


} 